/**
 * The guided tour's persisted state (spec §2.6) — pure transitions plus a
 * tiny storage adapter, no DOM.
 *
 * One localStorage key, `ql-tour-v1`, holds `{ status, stepIdx, version }`:
 *
 * - `never`   — nothing decided yet; the app may offer the welcome card once.
 * - `active`  — mid-tour; `stepIdx` is where a reload resumes.
 * - `skipped` — "Not now" / Skip tour / Esc. Never auto-offered again; the
 *               sidebar's Guide restarts it from the beginning.
 * - `done`    — walked to the end.
 *
 * Storage access is wrapped in try/catch everywhere: Safari private mode and
 * blocked site data throw, and a tour must never take the app down with it.
 */
import type { StorageLike } from "../trackedJobs";
import { finishedAfter, nextIndex, prevIndex, type TourStep } from "./steps";

export const TOUR_STATE_KEY = "ql-tour-v1";

export type TourStatus = "never" | "active" | "skipped" | "done";

export interface TourState {
  status: TourStatus;
  stepIdx: number;
  version: 1;
}

export const INITIAL_TOUR_STATE: TourState = { status: "never", stepIdx: 0, version: 1 };

const STATUSES: readonly TourStatus[] = ["never", "active", "skipped", "done"];

function isTourState(v: unknown): v is TourState {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return (
    o.version === 1 &&
    typeof o.status === "string" &&
    (STATUSES as readonly string[]).includes(o.status) &&
    typeof o.stepIdx === "number" &&
    Number.isInteger(o.stepIdx) &&
    o.stepIdx >= 0
  );
}

/** Read the persisted state; anything missing, malformed, from another
 *  version, or unreadable reads as "never". */
export function loadTourState(storage: StorageLike): TourState {
  try {
    const raw = storage.getItem(TOUR_STATE_KEY);
    if (!raw) return INITIAL_TOUR_STATE;
    const parsed: unknown = JSON.parse(raw);
    return isTourState(parsed) ? { status: parsed.status, stepIdx: parsed.stepIdx, version: 1 } : INITIAL_TOUR_STATE;
  } catch {
    return INITIAL_TOUR_STATE;
  }
}

/** Persist; a storage that refuses is ignored (the in-memory state still
 *  drives the session — only the resume-after-reload is lost). */
export function saveTourState(storage: StorageLike, state: TourState): void {
  try {
    storage.setItem(TOUR_STATE_KEY, JSON.stringify(state));
  } catch {
    /* private mode / quota — nothing to do */
  }
}

/** Always from the beginning — the Guide entry and the welcome card both
 *  mean "show me the whole thing". */
export function startTour(): TourState {
  return { status: "active", stepIdx: 0, version: 1 };
}

export function skipTour(state: TourState): TourState {
  return { ...state, status: "skipped" };
}

export function finishTour(state: TourState): TourState {
  return { ...state, status: "done", stepIdx: 0 };
}

/** Next available step, or `done` when there is none. No-op unless active. */
export function advance(state: TourState, steps: readonly TourStep[], hasVideo: boolean): TourState {
  if (state.status !== "active") return state;
  const next = nextIndex(state.stepIdx, steps, hasVideo);
  return next === null ? finishTour(state) : { ...state, stepIdx: next };
}

/** Previous available step; stays put on the first one. No-op unless active. */
export function back(state: TourState, steps: readonly TourStep[], hasVideo: boolean): TourState {
  if (state.status !== "active") return state;
  const prev = prevIndex(state.stepIdx, steps, hasVideo);
  return prev === null ? state : { ...state, stepIdx: prev };
}

/** True when the active step is the last one the user will see — the card
 *  then reads "Done" instead of "Next". */
export function isLastStep(state: TourState, steps: readonly TourStep[], hasVideo: boolean): boolean {
  return finishedAfter(state.stepIdx, steps, hasVideo);
}
